<!-- **** --------------------------------------------------------------------------
	SHARED SCRIPT
-------------------------------------------------------------------------- **** -->
<script lang="ts">
    import vm, { GridType, JourneyLine, JourneyLines, JourneyNode, JourneyNodes, JourneyTextNode, LineConnection, ZDist } from '@journeys/Journeys.viewmodel';
    import { computed, onMounted, onUnmounted, reactive, Ref, ref, toRaw, watch } from 'vue';
    // import { versionManager } from '@journeys/utils/VersionManager'

    // directives

    // utils
    import { cancelFrame, requestFrame } from '@utils';

    import InlineWorker from '@shared/modules/InlineWorker';

    // components
    import { NodeType } from '@journeys/Enums';
    import CanvasLayerLines from './CanvasLayerLines.vue';
    import CanvasLayerNodes from './CanvasLayerNodes.vue';
    import SelectionMenu from './SelectionMenu.vue';
    import TextMenu from './TextMenu.vue';
    import VectorLayer from './VectorLayer.vue';

    interface SelectionRect {
        h: number;
        w: number;
        x: number;
        y: number;
        ox: number;
        oy: number;
        rect?: DOMRect;
    }

    // this is mostly for debugging, otherwise components are Anonymous
    const component = {
        name: 'Canvas_Main',
    };
    export default {
        name: component.name,
    };
</script>

<!-- **** --------------------------------------------------------------------------
	SCRIPT
-------------------------------------------------------------------------- **** -->
<script lang="ts" setup>
    // const props = defineProps<{
    // z_dist: string
    // }>()
    // emit = defineEmits<{
    //    (ev: 'update:z_dist', value: number): void
    // }>()

    const { canvas, nav, viewport, editor, settings, activeJourney } = vm,
        /** local state */
        state: any = reactive({
            busy: false,
        }),
        canvasRef = ref(null);

    let nodeList: JourneyNodes = activeJourney.nodes,
        lineList: JourneyLines = activeJourney.lines;

    onMounted(() => {
        canvas.el = canvasRef.value!;
        editor.calcNodeArea();
    });

    watch(
        () => activeJourney.nodes,
        (newVal, oldVal) => {
            nodeList = newVal;
        }
    );

    onUnmounted(() => {
        editor.state.nodeObserver = {};
    });

    // selection tools and methods
    let sr: SelectionRect = reactive({
        ox: 0,
        oy: 0,
        x: 0,
        y: 0,
        h: 0,
        w: 0,
    });

    /** Viewport/canvas navigation */
    let eventPreventDefault: boolean = false,
        frameReq: number,
        // navActive = ref(false),
        busyTimer: any = null;

    //*** shortnaming variables is reads more like math
    //*** and is easier for me when handling graphical properties
    /** property window (width)  */
    let pw: number,
        /** half viewport width */
        hvw: number,
        /** half viewport height */
        hvh: number;
    /** left x */
    let lx: number,
        /** right x */
        rx: number,
        /** top x */
        ty: number,
        /** btm x */
        by: number;

    let zd = ZDist.Middle;

    const zs_1 = nav.zmin - nav.zstep,
        zs_2 = zs_1 - nav.zstep;

    let delta_zmod = 1,
        pw_mod = 0,
        z_span = (nav.zmin - nav.zmax) / 1000;
    /** main navigation method */
    const handleNav = (ev: WheelEvent) => {
        ev.stopPropagation();
        // editor.state.busy = true

        // TODO: the timer throttle needs to be changed
        if (!busyTimer) {
            nav.active = true;

            pw = nav.zscale_up(editor.state.showPropertyWindow ? viewport.computed.value.raw_pws / 2 : 0);
            hvw = nav.zscale_up(viewport.computed.value.half_w);
            hvh = nav.zscale_up(viewport.computed.value.half_h);

            lx = -(layer_l.value - pw) + hvw;
            rx = layer_r.value + pw - hvw;
            ty = -layer_t.value + hvh;
            by = layer_b.value - hvh;

            pw_mod = editor.state.showPropertyWindow ? viewport.computed.value.raw_pws / 4 : 0;
        } else clearTimeout(busyTimer);

        busyTimer = setTimeout(() => {
            nav.active = false;
            busyTimer = null;
        }, 400);

        cancelFrame(frameReq);
        frameReq = requestFrame(() => {
            if (ev.altKey) {
                ev.preventDefault();

                window.addEventListener(
                    'keyup',
                    (ev: any) => {
                        ev.preventDefault();
                    },
                    { once: true }
                );

                let delta = ~~(ev.deltaY * (1.5 - (nav.z - nav.zmax) / 2000)),
                    delta_wall = 0;

                if (nav.z >= nav.zmax + delta && nav.z <= nav.zmin + delta) nav.z -= delta;
                else {
                    if (nav.z > nav.zmax + delta) {
                        delta_wall = (nav.z - nav.zmax) / delta;
                        nav.z = nav.zmin;
                    } else {
                        delta_wall = (nav.z - nav.zmax) / delta;
                        nav.z = nav.zmax;
                    }
                }

                if (zd !== ZDist.Close && nav.z > zs_1) {
                    canvas.el?.classList.remove(ZDist.Far, ZDist.Middle);
                    canvas.el?.classList.add(ZDist.Close);

                    zd = ZDist.Close;
                } else if (zd !== ZDist.Far && nav.z < zs_2) {
                    canvas.el?.classList.remove(ZDist.Close, ZDist.Middle);
                    canvas.el?.classList.add(ZDist.Far);

                    zd = ZDist.Far;
                } else if (zd !== ZDist.Middle && nav.z >= zs_2 && nav.z <= zs_1) {
                    canvas.el?.classList.remove(ZDist.Far, ZDist.Close);
                    canvas.el?.classList.add(ZDist.Middle);

                    zd = ZDist.Middle;
                }

                let nx = ((ev.x - viewport.computed.value.half_w - pw_mod) / nav.sensitivity) * -delta,
                    ny = ((-70 + ev.y - viewport.computed.value.half_h) / nav.sensitivity) * -delta;

                if (delta_wall) {
                    nx = nav.z < 0 ? nx * delta_wall : nx / delta_wall;
                    ny = nav.z < 0 ? ny * delta_wall : ny / delta_wall;
                } else if (nav.z >= nav.zmin || nav.z <= nav.zmax) return;

                nav.x += ~~nx;
                if (nav.x < lx) nav.x = lx;
                if (nav.x > rx) nav.x = rx;

                nav.y += ~~ny;
                if (nav.y < ty) nav.y = ty;
                if (nav.y > by) nav.y = by;

                return;
            }

            nav.x += ~~((ev.shiftKey ? ev.deltaY : ev.deltaX) * (1.5 - (nav.z - nav.zmax) / 2000));
            if (nav.x < lx) nav.x = lx;
            if (nav.x > rx) nav.x = rx;

            if (!ev.shiftKey) {
                nav.y += ~~(ev.deltaY * (1.5 - (nav.z - nav.zmax) / 2000));
                if (nav.y < ty) nav.y = ty;
                if (nav.y > by) nav.y = by;
            }
        });
    };

    let dragFrameReq = 0;
    const handleDragNav = (ev: PointerEvent) => {
        let triggerMarginPassed = false;

        editor.state.busy = true;
        canvas.el?.setPointerCapture(ev.pointerId);

        let o = {
            x: ev.x,
            y: ev.y,
            ny: nav.y,
            nx: nav.x,
        };

        pw = nav.zscale_up(editor.state.showPropertyWindow ? viewport.computed.value.raw_pws / 2 : 0);
        hvw = nav.zscale_up(viewport.computed.value.half_w);
        hvh = nav.zscale_up(viewport.computed.value.half_h);

        lx = -(canvas.ext.l + canvas.sw - pw) + hvw;
        rx = canvas.ext.r + canvas.sw + pw - hvw;
        ty = -(canvas.ext.t + canvas.sh) + hvh;
        by = canvas.ext.b + canvas.sh - hvh;

        const handlePointerMove = (ev: PointerEvent) => {
            if (!triggerMarginPassed && Math.abs(o.x - ev.offsetX) + Math.abs(o.y - ev.offsetY) > 10) {
                triggerMarginPassed = true;
            }

            cancelFrame(dragFrameReq);
            dragFrameReq = requestFrame(() => {
                nav.x = o.nx + nav.zscale_up(o.x - ev.x);
                if (nav.x < lx) nav.x = lx;
                if (nav.x > rx) nav.x = rx;

                nav.y = o.ny + nav.zscale_up(o.y - ev.y);
                if (nav.y < ty) nav.y = ty;
                if (nav.y > by) nav.y = by;
            });
        };

        addEventListener('pointermove', handlePointerMove);
        addEventListener(
            'pointerup',
            () => {
                let evt = ev.target as HTMLElement;
                if (!triggerMarginPassed && /canvas-node|canvas-text/.test(evt.dataset.nodetype || ''))
                    editor.tools.sel_nodes = { [evt.dataset.id!]: activeJourney.nodes[evt.dataset.id!] };

                editor.state.busy = false;
                removeEventListener('pointermove', handlePointerMove);
            },
            { once: true }
        );
    };

    type SelDataResult = {
        add: number[];
        del: number[];
    };

    let worker_frameReq = 0;
    /** a separate thread for handling hitdetection
     * TODO: use transferable object (some caveats with CORS need to be handled first)
     */
    const worker = new InlineWorker(function (this: any, { data: { req_n, hook, payload } }: any) {
        //TODO: !!!
        //!!!! Known issue
        //!!!! while holding shift, any created element will be unmovable until reselect

        let defaultMargin = 6;

        if (hook === 'set') {
            this.store = {
                graph_data: payload, // items
                nodes: payload.node_data,
                nodes_arr: Object.values(payload.node_data),
                points: payload.point_data,
                points_arr: Object.values(payload.point_data),
                lines: payload.line_data,
                lines_arr: Object.values(payload.line_data),
                sel_nids: payload.sel_nids || {},
                sel_nodes: {},
                sel_pids: payload.sel_pids || {},
                sel_points: {},
                sel_lids: payload.sel_lids || {},
                sel_lines: {},
                defaultMargin: payload.margin || defaultMargin,
            };

            postMessage({ req_n, payload: 'ok' });
        }

        if (hook === 'hittest') {
            let { nodes, points, lines, nodes_arr, points_arr, lines_arr, sel_nodes, sel_points, sel_lines, sel_nids, sel_pids, sel_lids } = this.store;

            const addLine = (line: any) => {
                line_res.add.push(line.id);
                sel_lids[line.id] = line.id;
                sel_lines[line.id] = line;
            };
            const delLine = (line: any) => {
                line_res.del.push(line.id);
                delete sel_lids[line.id];
                delete sel_lines[line.id];
            };

            let sr = payload.sr, // selection rectangle
                line_res: SelDataResult = { add: [], del: [] },
                node_res: SelDataResult = { add: [], del: [] },
                point_res: SelDataResult = { add: [], del: [] };

            for (let n of nodes_arr) {
                let hittest = n.r > sr.x && n.x < sr.x + sr.w && n.b > sr.y && n.y < sr.y + sr.h;

                if (hittest) {
                    if (sel_nids[n.id]) continue;

                    node_res.add.push(n.id);
                    sel_nids[n.id] = n.id;
                    sel_nodes[n.id] = n;

                    // handle lines
                    for (let to in n.c_to) {
                        let line = lines[to];

                        if (
                            (sel_nids[line.c_to] && sel_nids[line.c_fr]) ||
                            (sel_nids[line.c_to] && sel_nids[line.c_fr]) ||
                            (sel_pids[`l${to}to`] && sel_pids[`l${to}fr`]) ||
                            (sel_pids[`l${to}to`] && sel_nids[line.c_fr]) ||
                            (sel_nids[line.c_to] && sel_pids[`l${to}fr`])
                        )
                            addLine(line);
                    }

                    for (let fr in n.c_fr) {
                        let line = lines[fr];

                        if (
                            (sel_nids[line.c_to] && sel_nids[line.c_fr]) ||
                            (sel_nids[line.c_to] && sel_nids[line.c_fr]) ||
                            (sel_pids[`l${fr}to`] && sel_pids[`l${fr}fr`]) ||
                            (sel_pids[`l${fr}to`] && sel_nids[line.c_fr]) ||
                            (sel_nids[line.c_to] && sel_pids[`l${fr}fr`])
                        )
                            addLine(line);
                    }
                } else if (!payload.shiftKey && sel_nids[n.id]) {
                    node_res.del.push(n.id);
                    delete sel_nids[n.id];
                    delete sel_nodes[n.id];

                    for (let lid in sel_lines) {
                        let line = lines[lid];

                        if (n.id === line.c_to || n.id === line.c_fr) delLine(lines[lid]);
                    }
                }
            }

            for (let p of points_arr) {
                let hittest = p.x >= sr.x && p.x <= sr.x + sr.w && p.y >= sr.y && p.y <= sr.y + sr.h;

                if (hittest) {
                    if (sel_pids[p.cid]) continue;

                    point_res.add.push(p.cid);
                    sel_pids[p.cid] = p.cid;
                    sel_points[p.cid] = p;

                    let line = lines[p.id];

                    if (
                        (sel_nids[line.c_to] && sel_nids[line.c_fr]) ||
                        (sel_nids[line.c_to] && sel_nids[line.c_fr]) ||
                        (sel_pids[`l${p.id}to`] && sel_pids[`l${p.id}fr`]) ||
                        (sel_pids[`l${p.id}to`] && sel_nids[line.c_fr]) ||
                        (sel_nids[line.c_to] && sel_pids[`l${p.id}fr`])
                    )
                        addLine(lines[p.id]);
                } else if (!payload.shiftKey && sel_pids[p.cid]) {
                    point_res.del.push(p.cid);
                    delete sel_pids[p.cid];
                    delete sel_points[p.cid];

                    for (let line_id in sel_lines) {
                        let lid = parseInt(line_id);

                        if (lid !== p.id) continue;

                        if (/(to|fr)$/.test(p.cid)) delLine(lines[lid]);
                    }
                }
            }

            if (node_res.add[0] || node_res.del[0] || point_res.add[0] || point_res.del[0] || line_res.add[0] || line_res.del[0]) {
                //    '\nnodes:', nodes,
                //    '\nlines:', lines,
                //    '\npoints:', points,
                //    '\nn_ids:', sel_nids,
                //    '\np_ids:', sel_pids,
                //    '\nl_ids:', sel_lids)

                let sel_dims = calcSelArea.call(this);

                postMessage({ req_n, payload: { node_res, point_res, line_res, sel_dims } });
            }
        }

        if (hook === 'calcselarea') {
            postMessage({ req_n, payload: { sel_dims: calcSelArea.call(this, payload) } });
        }

        function calcSelArea(this: any, sel_lists?: any) {
            let sel_dims: any = {};

            const checkItemPos = (i: any, margin: number = 0) => {
                let x: number = i.x,
                    y: number = i.y,
                    h: number = margin || i.h,
                    w: number = margin || i.w;

                sel_dims.h ||= h;
                sel_dims.w ||= w;
                sel_dims.t ||= y;
                sel_dims.r ||= x + w;
                sel_dims.b ||= y + h;
                sel_dims.l ||= x;

                if (x - margin < sel_dims.l) sel_dims.l = x - (margin && w);
                if (x + w > sel_dims.r) {
                    sel_dims.r = x + w;
                }

                if (y - margin < sel_dims.t) sel_dims.t = y - (margin && h);
                if (y + h > sel_dims.b) {
                    sel_dims.b = y + h;
                }
            };

            let margin = this.store?.defaultMargin || defaultMargin;
            if (sel_lists) {
                for (let n in sel_lists.node_data) checkItemPos(sel_lists.node_data[n]);

                for (let key in sel_lists.line_data) {
                    let l = sel_lists.line_data[key];

                    for (let p of l.points) checkItemPos(sel_lists.point_data[p], margin);

                    let line_end = {
                        x: l.ex,
                        y: l.ey,
                    };

                    checkItemPos(line_end, margin);

                    let line_start = {
                        x: l.x,
                        y: l.y,
                    };

                    checkItemPos(line_start, margin);
                }

                for (let p in sel_lists.point_data) checkItemPos(sel_lists.point_data[p], margin);
            } else {
                for (let key in this.store.sel_nids) {
                    let n = this.store.nodes[key];

                    checkItemPos(n);
                }

                for (let key in this.store.sel_pids) {
                    let p = this.store.points[key];

                    checkItemPos(p, margin);
                }

                for (let key in this.store.sel_lids) {
                    let l = this.store.lines[key];

                    for (let p of l.points) checkItemPos(this.store.points[p], margin);

                    let line_end = {
                        x: l.ex,
                        y: l.ey,
                    };

                    checkItemPos(line_end, margin);

                    let line_start = {
                        x: l.x,
                        y: l.y,
                    };

                    checkItemPos(line_start, margin);
                }
            }

            sel_dims.h = sel_dims.b - sel_dims.t;
            sel_dims.w = sel_dims.r - sel_dims.l;

            return sel_dims;
        }
    });

    editor.events.addListener('calcselarea', (data: any) => {
        let { line_data, point_data } = mapLinePointData(Object.values(data.lines));

        for (let p_cid in data.points) {
            let point = data.points[p_cid];

            point_data[point.cid] = toRaw(point);
        }

        worker
            .run('calcselarea', {
                node_data: mapNodeData(Object.values(data.nodes)),
                point_data,
                line_data,
            })
            .then(({ sel_dims }: any) => {
                cancelFrame(worker_frameReq);
                requestFrame(() => {
                    editor.tools.sel_dims = sel_dims;
                });
            });
    });

    // sendable worker data
    const mapNodeData = (nodes: any = Object.values(activeJourney.nodes)) => {
        return nodes.reduce((acc: any, item: JourneyNode & JourneyTextNode) => {
            let h = item.h || item.runtime?.h || 0,
                w = item.w || item.runtime?.w || 0;

            acc[item.id] = {
                id: item.id,
                x: item.x,
                y: item.y,
                h,
                w,
                c_to: item.lnto ? toRaw(item.lnto) : {},
                c_fr: item.lnfr ? toRaw(item.lnfr) : {},
            };
            acc[item.id].r = item.x + w;
            acc[item.id].b = item.y + h;

            return acc;
        }, {});
    };

    const mapLinePointData = (lines: any = Object.values(activeJourney.lines)) => {
        return lines.reduce(
            (acc: any, item: JourneyLine) => {
                acc.line_data[item.id] = {
                    id: item.id,
                    c_to: item.to,
                    c_fr: item.fr,
                    points: [],
                };

                item.points?.forEach(({ x, y }: { x: number; y: number }, idx) => {
                    let cid = `l${item.id}p${idx}`;
                    acc.point_data[cid] = {
                        cid,
                        id: item.id,
                        idx: idx,
                        x,
                        y,
                    };

                    acc.line_data[item.id].points.push(cid);
                });

                if (!item.to) {
                    let cid = `l${item.id}to`;
                    acc.point_data[cid] = {
                        cid,
                        id: item.id,
                        idx: 'to',
                        x: item.ex,
                        y: item.ey,
                    };
                }

                if (!item.fr) {
                    let cid = `l${item.id}fr`;
                    acc.point_data[cid] = {
                        cid,
                        id: item.id,
                        idx: 'fr',
                        x: item.x,
                        y: item.y,
                    };
                }

                return acc;
            },
            { line_data: {}, point_data: {} }
        );
    };

    const sel_ids = computed(() => {
        return {
            nids: Object.values(editor.tools.sel_nodes).reduce((acc: any, n: any) => ((acc[n.id] = n.id), acc), {}),
            pids: Object.values(editor.tools.sel_points).reduce((acc: any, p: any) => ((acc[p.cid] = p.cid), acc), {}),
            lids: Object.values(editor.tools.sel_lines).reduce((acc: any, l: any) => ((acc[l.id] = l.id), acc), {}),
        };
    });

    let selectionRectActive: Ref<boolean | null> = ref(null),
        selectionFramReq = 0,
        selectionWorkerFramReq = 0;
    const startSelect = (ev: PointerEvent) => {
        canvas.el?.setPointerCapture(ev.pointerId);

        let evt = ev.target as HTMLElement,
            evt_type = evt.dataset.nodetype;

        if (!/canvas$/.test(evt_type!)) return;

        editor.state.selecting = true;
        sr.x = sr.ox = ~~ev.offsetX;
        sr.y = sr.oy = ~~ev.offsetY;

        viewport.closeMenu('all');

        let triggerMarginPassed = false;

        let { line_data, point_data } = mapLinePointData(),
            payload: any = {
                node_data: mapNodeData(),
                line_data,
                point_data,
            };

        if (ev.shiftKey) {
            payload.sel_nids = sel_ids.value.nids;
            payload.sel_pids = sel_ids.value.pids;
            payload.sel_lids = sel_ids.value.lids;
        }

        // set start data on different thread
        worker.run('set', payload).then((data: any) => {
            // ...
        });

        const handleSelect = (ev: any) => {
            if (!triggerMarginPassed && Math.abs(sr.ox - ev.offsetX) + Math.abs(sr.oy - ev.offsetY) > 10) {
                triggerMarginPassed = true;
                selectionRectActive.value = true;
            }

            //** VENDOR BUG: firefox nullifies ev.currentTarget property after requestFrame
            //** TEMP_FIX: cache values prior next frame
            let ev_y = ev.offsetY,
                ev_x = ev.offsetX;

            // cancelFrame(selectionFramReq)
            // selectionFramReq = requestFrame(() => {
            // sr.x = sr.ox
            // sr.y = sr.oy
            sr.h = Math.abs(ev_y - sr.oy);
            sr.w = Math.abs(ev_x - sr.ox);

            if (ev_x < sr.ox) {
                sr.x = ev_x;
                sr.h = Math.abs(ev_y - sr.oy);
            }
            if (ev_y < sr.oy) {
                sr.y = ev_y;
                sr.w = Math.abs(ev_x - sr.ox);
            }
            // })

            worker.run('hittest', { sr: toRaw(sr), shiftKey: ev.shiftKey }).then((data: any) => {
                if (!data) return;
                editor.state.showNodeMenu = false;

                let { node_res, point_res, line_res, sel_dims } = data;

                // cancelFrame(selectionWorkerFramReq)
                // selectionWorkerFramReq = requestFrame(() => {
                if (!ev.shiftKey) {
                    for (let n_id of node_res.del) delete editor.tools.sel_nodes[n_id];

                    for (let p_id of point_res.del) delete editor.tools.sel_points[p_id];

                    for (let l_id of line_res.del) delete editor.tools.sel_lines[l_id];
                }

                for (let n_id of node_res.add) editor.tools.sel_nodes[n_id] = nodeList[n_id];

                for (let p_id of point_res.add) editor.tools.sel_points[p_id] = point_data[p_id];

                for (let l_id of line_res.add) editor.tools.sel_lines[l_id] = lineList[l_id];

                editor.tools.sel_dims = sel_dims;
            });
            // })
        };

        canvas.el?.addEventListener('pointermove', handleSelect);
        document.addEventListener(
            'pointerup',
            () => {
                editor.state.selecting = false;

                cancelFrame(selectionFramReq);
                sr.h = 0;
                sr.w = 0;
                selectionRectActive.value = false;
                canvas.el?.removeEventListener('pointermove', handleSelect);
            },
            { once: true }
        );
    };

    const newNode = ({ vd, data }: any) => {
        let id = editor.tools.seq_iid(),
            newnode: JourneyNode = {
                ...vd,
                id: id,
                lnto: {},
                lnfr: {},

                data,
                runtime: {
                    cache: {},
                },
            };

        if (newnode.data.type === NodeType.Segment)
            if (!activeJourney.startingNode || !activeJourney.nodes[activeJourney.startingNode]) {
                newnode.data.isInitial = true;
                newnode.data.startingSegment = {
                    Name: null,
                    Type: 'SegmentGroup',
                    Bool: 2,
                    Segments: [],
                };

                activeJourney.startingNode = id;
            } else return;

        nodeList[id] = newnode;

        requestFrame(() => editor.calcNodeArea());
    };

    editor.events.addListener('newnode', newNode);

    const addNodes = (nodes: any) => {
        Object.assign(nodeList, nodes);

        editor.events.emit('clearselection');

        editor.tools.sel_nodes = { ...nodes };
        requestFrame(() => editor.calcNodeArea());
        editor.events.emit('calcselarea', {
            nodes: editor.tools.sel_nodes,
            points: {},
            lines: {},
        });
    };

    editor.events.addListener('addnodes', addNodes);

    const deleteNode = (id: string) => {
        let node = nodeList[id];

        if (!node) return;

        activeJourney.steps &&= {};

        if (node.data.type !== NodeType.Text) {
            node = node as JourneyNode;

            if (node.data.isInitial) activeJourney.startingNode = null;

            Object.values({ ...node.lnto, ...node.lnfr }).forEach((lineConnection: LineConnection) => {
                let line = lineList[lineConnection.id];
                if (!line) return;

                if (line.to) {
                    let cn = nodeList[line.to] as JourneyNode;
                    delete cn.lnto[line.id];
                    delete cn.lnfr[line.id];
                }
                if (line.fr) {
                    let cn = nodeList[line.fr] as JourneyNode;
                    delete cn.lnto[line.id];
                    delete cn.lnfr[line.id];
                }

                delete lineList[line.id];
            });
        }

        delete nodeList[id];

        viewport.closeMenu('contextmenu');
    };

    editor.events.addListener('deletenode', deleteNode);

    const textNodeFactory = (x: number, y: number, text: string = 'Text'): JourneyTextNode => ({
        id: editor.tools.seq_iid(),
        x,
        y,
        h: 0,
        w: 0,
        fc: [0, 0, 0], // font color
        fs: 20, // font size
        fw: 400, // font weight
        fv: 0, // font variation [0 == normal, 1 == italic]
        fa: 0,
        data: {
            type: NodeType.Text,
            text,
        },
    });

    const handleAddTextNode = (ev: PointerEvent) => {
        let evt = ev.target as HTMLElement,
            evt_type = evt.dataset.nodetype;

        if (!/canvas$/.test(evt_type!)) return;

        let newTextNode = textNodeFactory(~~ev.offsetX, ~~ev.offsetY);

        activeJourney.nodes[newTextNode.id] = newTextNode;

        viewport.closeMenu('all');
    };

    let handleLeftPointerDown = (ev: PointerEvent) => {
        let evt = ev.target as HTMLElement,
            evt_type = evt.dataset.nodetype || '';

        viewport.closeMenu('all');

        if (!/canvas/.test(evt_type!)) return;
        if (
            (!/canvas-node|canvas-text/.test(evt_type) &&
                ((/nav/.test(editor.state.activeTool) && ev.altKey) || (/pointer/.test(editor.state.activeTool) && !ev.altKey))) ||
            /line/.test(editor.state.activeTool)
        ) {
            if (!ev.shiftKey) {
                editor.events.emit('clearselection');
            }
        }

        switch (editor.state.activeTool) {
            case 'pointer':
                if (ev.altKey) handleDragNav(ev);
                else startSelect(ev);
                break;
            case 'nav':
                if (ev.altKey) startSelect(ev);
                else handleDragNav(ev);
                break;
            case 'line':
                let connecting;
                if (evt_type === 'canvas-node' && evt.dataset.id) {
                    connecting = {
                        direction: 'to',
                        node: nodeList[evt.dataset.id],
                    };
                } else if (evt_type === 'canvas-text' && evt.dataset.id) return;

                editor.events.emit('drawline', ev, connecting);
                break;
            case 'text':
                handleAddTextNode(ev);
                break;
        }
    };

    const setObserver = (el: any, id: any) => {
        editor.state.nodeObserver[id] = el;
    };

    const canvasGrid: { [key: string]: any } = {
        'top-left': {
            rows: canvas.sections.t,
            columns: canvas.sections.l,
        },
        'top-right': {
            rows: canvas.sections.t,
            columns: canvas.sections.r,
        },
        'btm-left': {
            rows: canvas.sections.b,
            columns: canvas.sections.l,
        },
        'btm-right': {
            rows: canvas.sections.b,
            columns: canvas.sections.r,
        },
    };

    // cache is good
    //* layer dims
    const layer_t = computed(() => canvas.sh + canvas.ext.t),
        layer_r = computed(() => canvas.sw + canvas.ext.r),
        layer_b = computed(() => canvas.sh + canvas.ext.b),
        layer_l = computed(() => canvas.sw + canvas.ext.l);

    //* canvas dims
    const canvas_border_top = computed(() => -canvas.rh / 2 - canvas.ext.t),
        canvas_border_right = computed(() => canvas.rw / 2 + canvas.ext.r),
        canvas_border_btm = computed(() => canvas.rh / 2 + canvas.ext.b),
        canvas_border_left = computed(() => -canvas.rw / 2 - canvas.ext.l);

    //* grid sizes
    const grid = computed(() => {
        let spacing = settings.grid.spacing,
            stroke = settings.grid.strokeWidth;

        if (state.z_dist === ZDist.Far) {
            spacing *= 2;
            stroke *= 2;
        }

        return {
            spacing,
            spacing_md: spacing * 2,
            spacing_xl: spacing * 10,
            stroke_xs: stroke / 2,
            stroke,
            stroke_xl: stroke * 2,
        };
    });
</script>

<!-- **** --------------------------------------------------------------------------
	TEMPLATE
-------------------------------------------------------------------------- **** -->
<template>
    <div
        key="canvas"
        class="canvas"
        :class="[
            zd,
            settings.grid.snap ? 'grid-snap' : '',
            editor.state.busy ? 'editor-busy' : '',
            editor.state.moving ? 'editor-moving' : '',
            editor.state.activeTool + '-tool',
        ]"
        data-nodetype="canvas"
        ref="canvasRef"
        :style="[
            {
                height: `${canvas.h.value / 2}px`,
                width: `${canvas.w.value / 2}px`,
                transform: `translate3d(${-nav.x}px, ${-nav.y}px, ${nav.z}px)`,
            },
            `--layer-t: ${layer_t}px`,
            `--layer-r: ${layer_r}px`,
            `--layer-b: ${layer_b}px`,
            `--layer-l: ${layer_l}px`,
        ]"
        @pointerdown.left="handleLeftPointerDown"
        @wheel.prevent="handleNav">
        <div
            class="canvas-wrapper"
            :style="[
                {
                    top: `${canvas_border_top}px`,
                    left: `${canvas_border_left}px`,
                },
            ]">
            <!-- currently not fully utilized, dedicated for wrapped drag and hiding elements beyond the viewport -->
            <template v-if="editor.dev.show.canvasGrid">
                <div v-for="({ rows, columns }, name) in canvasGrid" :key="`CSC${name}`" class="coordinal-layer" :class="name" :style="[`grid-area: ${name}`]">
                    <div
                        v-for="idx of rows * columns"
                        :key="idx + 'cs' + name"
                        class="canvas-section"
                        :style="{
                            height: `${canvas.sh}px`,
                            width: `${canvas.sw}px`,
                        }">
                        <h2>{{ idx }}</h2>
                    </div>
                </div>
            </template>
        </div>

        <svg class="canvas-grid" xmlns="http://www.w3.org/2000/svg" overflow="hidden">
            <defs>
                <!-- grid type: none -->
                <pattern
                    v-if="settings.grid.type === GridType.None"
                    id="smallGrid"
                    :height="grid.spacing"
                    :width="grid.spacing"
                    patternUnits="userSpaceOnUse"></pattern>
                <!-- grid type: dot -->
                <pattern v-if="settings.grid.type === GridType.Dot" id="smallGrid" :height="grid.spacing" :width="grid.spacing" patternUnits="userSpaceOnUse">
                    <g :fill="settings.grid.dotColor">
                        <circle cx="0" cy="0" :r="grid.stroke_xs" />
                        <circle :cx="grid.spacing" cy="0" :r="grid.stroke_xs" />
                        <circle :cx="grid.spacing_md" cy="0" :r="grid.stroke_xs" />

                        <circle cx="0" :cy="grid.spacing" :r="grid.stroke_xs" />
                        <circle :cx="grid.spacing" :cy="grid.spacing" :r="grid.stroke_xs" />
                        <circle :cx="grid.spacing_md" :cy="grid.spacing" :r="grid.stroke_xs" />

                        <circle cx="0" :cy="grid.spacing_md" :r="grid.stroke_xs" />
                        <circle :cx="grid.spacing" :cy="grid.spacing_md" :r="grid.stroke_xs" />
                        <circle :cx="grid.spacing_md" :cy="grid.spacing_md" :r="grid.stroke_xs" />
                    </g>
                </pattern>

                <!-- grid type: pattern & solid -->
                <pattern v-else id="smallGrid" :height="grid.spacing" :width="grid.spacing" patternUnits="userSpaceOnUse">
                    <path :d="`M ${grid.spacing} 0 L 0 0 0 ${grid.spacing}`" :stroke="settings.grid.color" :stroke-width="grid.stroke" fill="none" />
                </pattern>

                <!-- main grid -->
                <pattern id="grid" :height="grid.spacing_xl" :width="grid.spacing_xl" patternUnits="userSpaceOnUse">
                    <rect :height="grid.spacing_xl" :width="grid.spacing_xl" fill="url(#smallGrid)" />

                    <!-- subpattern -->
                    <path
                        v-if="settings.grid.type === GridType.Pattern"
                        :d="`M ${grid.spacing_xl} 0 L 0 0 0 ${grid.spacing_xl}`"
                        :stroke="settings.grid.color"
                        :stroke-width="grid.stroke_xl"
                        fill="none" />
                </pattern>
            </defs>

            <!-- grid container -->
            <rect id="guideGrid" :x="canvas_border_left" :y="canvas_border_top" width="200%" height="200%" fill="url(#grid)" />

            <!-- center lines -->
            <template v-if="settings.grid.type === GridType.Pattern">
                <line
                    class="grid-vertical-line"
                    :x1="0"
                    :y1="canvas_border_top"
                    :x2="0"
                    :y2="canvas_border_btm"
                    :stroke="settings.grid.centerLineColor"
                    :stroke-width="grid.stroke" />
                <line
                    id="grid-horizon"
                    :x1="canvas_border_left"
                    :y1="0"
                    :x2="canvas_border_right"
                    :y2="0"
                    :stroke="settings.grid.centerLineColor"
                    :stroke-width="grid.stroke" />
            </template>
        </svg>

        <!-- node layer (the nesting allows to cheaply get the outer dimensions of the used canvas area through scroll-dimension property.. cheaper than the native intersection observer, but the nesting can become a problem if we start dealing with a lot of elements..) -->
        <div
            class="node-layer area-btm"
            :class="editor.dev.show.pseudoHelpers && 'dev_show-helpers'"
            data-pos="btm"
            v-bind="!editor.state.nodeObserver['b'] ? ({ ref: (el: HTMLElement) => setObserver(el, 'b') } as any) : null">
            <div
                class="area-left"
                data-pos="left"
                v-bind="!editor.state.nodeObserver['l'] ? ({ ref: (el: HTMLElement) => setObserver(el, 'l') } as any) : null">
                <div
                    class="area-top"
                    data-pos="top"
                    v-bind="!editor.state.nodeObserver['t'] ? ({ ref: (el: HTMLElement) => setObserver(el, 't') } as any) : null">
                    <div
                        class="area-right"
                        data-pos="right"
                        v-bind="!editor.state.nodeObserver['r'] ? ({ ref: (el: HTMLElement) => setObserver(el, 'r') } as any) : null">
                        <CanvasLayerNodes :data="nodeList" :lineList="lineList" />
                    </div>
                </div>
            </div>
        </div>

        <!-- line layer -->
        <div class="path-layer">
            <CanvasLayerLines :data="lineList" :nodeList="nodeList" />
        </div>

        <!-- selection optimization layer -->
        <div
            class="vector-layer pe-none"
            v-if="editor.tools.sel_count > 1"
            :style="`transform: translate3d(${editor.dev.props.sel_x}px, ${editor.dev.props.sel_y}px, 0)`">
            <!-- <CanvasLayerNodes :data="editor.tools.sel_nodes" :lineList="lineList" /> -->
            <VectorLayer :nodes="editor.tools.sel_nodes" :data="editor.tools.sel_lines" :nodeList="nodeList" />
        </div>

        <transition name="quick-fade">
            <div
                v-if="editor.tools.sel_count > 1 && !editor.state.moving"
                class="selection-area"
                :style="{
                    top: editor.tools.sel_dims.t - editor.tools.sel_areaPadding + 'px',
                    left: editor.tools.sel_dims.l - editor.tools.sel_areaPadding + 'px',
                    height: editor.tools.sel_dims.h + editor.tools.sel_areaPadding * 2 + 'px',
                    width: editor.tools.sel_dims.w + editor.tools.sel_areaPadding * 2 + 'px',
                }"></div>
        </transition>
        <transition name="quick-fade">
            <SelectionMenu
                v-if="editor.tools.sel_count > 1 && !editor.state.selecting && !editor.state.moving"
                :style="`top: ${editor.tools.sel_dims.t - editor.tools.sel_areaPadding - 60}px; left: ${
                    editor.tools.sel_dims.l + editor.tools.sel_dims.w / 2 - editor.tools.sel_areaPadding * 1.75
                }px; --zscale: ${1 * ((nav.p - nav.z) / nav.p)};`"></SelectionMenu>
        </transition>
        <transition name="quick-fade">
            <TextMenu
                v-if="
                    editor.tools.sel_count === 1 &&
                    !editor.state.moving &&
                    editor.tools.sel_keyObj &&
                    activeJourney.nodes[editor.tools.sel_keyObj].data.type === NodeType.Text
                "
                :node="activeJourney.nodes[editor.tools.sel_keyObj] as JourneyTextNode"></TextMenu>
        </transition>

        <!-- selection rectangle -->
        <div
            class="selection-rect"
            :style="{
                height: `${sr.h}px`,
                width: `${sr.w}px`,
                transform: `translate3d(${sr.x}px,${sr.y}px, 0)`,
            }"></div>

        <!-- observer visualization, don't compile to prod.. wasted performance -->
        <div
            v-if="editor.dev.show.nodeArea"
            class="occupied-node-area dev_show"
            :style="{
                height: `${editor.state.nodeArea.h}px`,
                width: `${editor.state.nodeArea.w}px`,
                transform: `translate3d(${-editor.state.nodeArea.l}px, ${-editor.state.nodeArea.t}px, 0)`,
            }"></div>
    </div>
</template>
<!-- **** --------------------------------------------------------------------------
	STYLES
-------------------------------------------------------------------------- **** -->
<style>
    .editor-busy .line {
        transition: d 0.12s ease-out;
    }
    .editor-busy .point {
        transition: cx 0.12s ease-out;
        transition-property: cx, cy;
    }
    .grid-snap.editor-busy :where(.node, .text-node, .vector-layer) {
        transition: transform 0.12s ease-out;
    }

    .pe-none {
        pointer-events: none;
    }

    /* z-dist affected elements */
    .ZD_CLOSE {
        --dist-m: 0.7;
        transform: scale(0.5);
    }

    .ZD_MIDDLE {
        --dist-m: 1;
        transform: scale(1);
    }

    .ZD_FAR {
        --dist-m: 1.4;
        transform: scale(1.5);
    }

    .ZD_SPACE {
        --dist-m: 1.8;
        transform: scale(2);
    }
    .ZD_CLOSE .node-title-wrapper {
        transform: scale(0.5);
    }

    .ZD_MIDDLE .node-title-wrapper {
        transform: scale(1);
    }

    .ZD_FAR .node-title-wrapper {
        transform: scale(1.5);
    }

    .ZD_SPACE .node-title-wrapper {
        transform: scale(2);
    }

    /* .ZD_CLOSE .point.selected {
   r: 4;
}

.ZD_CLOSE .point:hover {
   r: 5;
}

.ZD_MIDDLE .point.selected {
   r: 5;
}

.ZD_MIDDLE .point:hover {
   r: 6;
}

.ZD_FAR .point.selected {
   r: 6;
}

.ZD_FAR .point:hover {
   r: 7;
} */

    .selection-area {
        position: absolute;

        border: 16px solid rgba(0, 0, 0, 0.01);
        outline: calc(2px * var(--dist-m)) solid #1fa9b07b;
        border-radius: 3px;

        pointer-events: none;

        transition: transform var(--td-normal) cubic-bezier(0.12, 0.12, 0.12, 1.03);
        transition-property: top, left, height, width;
    }

    /* TODO: find this a good home */
    .component-busy {
        pointer-events: none !important;
        user-select: none !important;
        transition-duration: 0s !important;
    }

    .canvas.editor-busy {
        transition-duration: 0s !important;
    }

    .occupied-node-area {
        visibility: hidden;
        display: flex;
        position: absolute;
        background: rgba(52, 117, 117, 0.347);
        transform: translate3d(0, 0, 20px);

        z-index: -2;
        /* transform-style:preserve-3d;
   transform: perspective(100) translate3d(0,0,100px); */
        pointer-events: none;
        user-select: none;
        opacity: 0;

        /* will-change: height, width, transform; */
    }

    .occupied-node-area.dev_show {
        visibility: visible;
        opacity: 1;
    }

    :where(.area-top, .area-right, .area-btm, .area-left) {
        color: black;
        position: absolute;

        transform: rotate(90deg);
        background-color: rgb(102, 138, 67);
        transform-origin: top left;
        z-index: 1;
        pointer-events: none;
        user-select: none;
    }

    :where(.area-top, .area-right, .area-btm, .area-left)::after {
        content: attr(data-pos);
        display: none;
        align-items: center;
        justify-content: center;
        font-size: 0.7rem;
        font-weight: 600;
        text-transform: uppercase;
        color: white;
        position: absolute;
        height: 9rem;
        width: 2.4rem;
        background-color: inherit;
        z-index: -4;
        pointer-events: none;
        user-select: none;
    }

    .area-top {
        background-color: green;
    }

    .area-right {
        background-color: red;
    }

    .area-left {
        background-color: blue;
    }

    .dev_show-helpers.area-btm::after,
    .dev_show-helpers :where(.area-top, .area-right, .area-btm, .area-left)::after {
        display: flex;
    }

    .path-layer {
        top: 0;
        left: 0;
        position: fixed;
        z-index: 3;
        user-select: none;
        /* pointer-events: none; */
    }

    .canvas {
        display: block;
        position: absolute;
        top: 50%;
        left: 50%;
        height: 1000px;
        width: 1500px;
        transition: transform var(--td-fast) ease-out;

        /* backface-visibility: hidden; */
        /* transform-style: preserve-3d; */
        user-select: none;
        /* pointer-events: none; */

        will-change: transform;

        z-index: 1;
    }

    .canvas.text-tool:hover {
        cursor: text;
    }

    .canvas::after {
        content: '';
        position: absolute;
        height: 200%;
        width: 200%;
        top: calc(var(--layer-t) * -1);
        left: calc(var(--layer-l) * -1);
        pointer-events: all;
        z-index: -1;
    }

    .canvas-wrapper {
        display: grid;
        grid-template-rows: var(--layer-t) var(--layer-b);
        grid-template-columns: var(--layer-l) var(--layer-r);
        grid-template-areas: 'top-left top-right' 'btm-left btm-right';

        position: absolute;
        align-items: start;
        justify-items: start;
        height: 200%;
        width: 200%;
        z-index: 2;
        pointer-events: all;
        pointer-events: none;

        -webkit-transform: rotate(0deg);
        -moz-transform: rotate(0deg);
        -ms-transform: rotate(0deg);
        -o-transform: rotate(0deg);
        transform: rotate(0deg);
    }

    /*
    .canvas-wrapper {
        outline: 0.1rem dashed rgb(69 72 114);
    }
    */

    /* adjusting for correct positioning */
    .coordinal-layer {
        display: flex;
        flex-wrap: wrap;
        position: absolute;
        height: 100%;
        width: 100%;
        pointer-events: none;
    }

    .coordinal-layer.top-left {
        transform: rotate(180deg);
    }

    .coordinal-layer.top-right {
        right: 100%;
        transform-origin: top right;
        transform: rotate(-90deg);
        height: var(--layer-r);
        width: var(--layer-t);
    }

    .top-right .section-container {
        flex-direction: row;
        height: var(--layer-r);
        width: var(--layer-t);
    }

    .coordinal-layer.btm-left {
        left: 100%;
        transform-origin: top left;
        transform: rotate(90deg);
        height: var(--layer-l);
        width: var(--layer-b);
    }

    .btm-left .section-container {
        flex-direction: row;
        height: var(--layer-l);
        width: var(--layer-b);
    }

    .canvas-section {
        display: flex;

        flex-direction: column;
        align-items: center;
        justify-content: center;
        position: relative;
        color: #ff000009;
        height: 100%;
        width: 100%;
        outline: 0.08rem dashed hsla(0, 55%, 21%, 0.541);
        outline-offset: -0.2rem;
        user-select: none;
        pointer-events: none;
    }

    .canvas-section h2 {
        font-size: 7.5rem;
    }

    .canvas-grid {
        display: block;
        position: absolute;
        height: 100%;
        width: 100%;
        user-select: none;
        pointer-events: none;
    }

    /* .canvas-grid {
   transform: translate3d(0, 0, 0);
   z-index: 3;
} */

    #guideGrid {
        pointer-events: none;

        /* temp */
        outline: 1px solid rgb(238, 220, 220);
        outline-offset: -0.5px;

        /* will-change: transform, height, width; */
    }

    .node-layer {
        position: absolute;
        z-index: 4;
    }

    .selection-rect {
        display: block;
        position: absolute;
        border: 1px solid #f17cb15b;
        background-color: #0b3f1611;
        height: 100%;
        width: 100%;
        padding: 0;
        border-radius: 0.15rem;
        opacity: 0.66;
        pointer-events: none;

        z-index: 20;
        will-change: height, width, transform;
    }

    /* dev */
    .devinfo {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate3d(-50%, -50%, 0);
    }

    .devinfo h5 {
        white-space: nowrap;
        margin: 0.2rem;
        font-size: 0.75rem;
    }
</style>
